import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);
export { AuthContext };

const API = process.env.REACT_APP_API_BASE_URL || '';
const CHANNEL_NAME = 'cancepro_auth';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(sessionStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [waitingForPeer, setWaitingForPeer] = useState(!sessionStorage.getItem('token'));
  const [emailVerified, setEmailVerified] = useState(
    () => sessionStorage.getItem('email_verified') === '1'
  );
  const channelRef = useRef(null);

  // ── BroadcastChannel: cross-tab session sharing ───────────────────────────
  useEffect(() => {
    const ch = new BroadcastChannel(CHANNEL_NAME);
    channelRef.current = ch;

    ch.onmessage = (e) => {
      const msg = e.data;
      if (msg.type === 'TOKEN_REQUEST') {
        // Another tab is asking for the token — send it if we have one
        const t = sessionStorage.getItem('token');
        const ev = sessionStorage.getItem('email_verified');
        if (t) {
          ch.postMessage({ type: 'TOKEN_RESPONSE', token: t, email_verified: ev });
        }
      } else if (msg.type === 'TOKEN_RESPONSE' && !sessionStorage.getItem('token')) {
        // We received the token from a sibling tab
        sessionStorage.setItem('token', msg.token);
        if (msg.email_verified) sessionStorage.setItem('email_verified', msg.email_verified);
        setToken(msg.token);
        setEmailVerified(msg.email_verified === '1');
        setWaitingForPeer(false);
      } else if (msg.type === 'LOGOUT') {
        // Another tab logged out — clear our session too
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('email_verified');
        setToken(null);
        setUser(null);
        setEmailVerified(false);
      }
    };

    // If this tab has no token, ask sibling tabs for one
    if (!sessionStorage.getItem('token')) {
      ch.postMessage({ type: 'TOKEN_REQUEST' });
      // Give sibling tabs time to respond before giving up
      const timeout = setTimeout(() => setWaitingForPeer(false), 500);
      return () => { clearTimeout(timeout); ch.close(); };
    }

    return () => ch.close();
  }, []); // runs once on mount

  const api = useCallback(() => {
    const instance = axios.create({ baseURL: API });
    if (token) {
      instance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
    return instance;
  }, [token]);

  // On mount / when token changes: validate against backend
  useEffect(() => {
    if (waitingForPeer) return; // still waiting for BroadcastChannel
    if (!token) {
      setLoading(false);
      return;
    }
    api()
      .get('/auth/profile')
      .then((res) => {
        setUser(res.data.user);
        const verified = !!res.data.user.email_verified;
        setEmailVerified(verified);
        sessionStorage.setItem('email_verified', verified ? '1' : '0');
      })
      .catch(() => {
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('email_verified');
        setToken(null);
        setUser(null);
        setEmailVerified(false);
      })
      .finally(() => setLoading(false));
  }, [token, api, waitingForPeer]);

  /** Step 1: send credentials → backend returns otp_token (not full session) */
  const login = async (identifier, password) => {
    const res = await axios.post(`${API}/auth/login`, { identifier, password });
    return res.data;
  };

  /** Step 2: called after OTP verification succeeds — stores the real session JWT */
  const completeOtpLogin = (data) => {
    sessionStorage.setItem('token', data.token);
    const verified = !!data.email_verified;
    sessionStorage.setItem('email_verified', verified ? '1' : '0');
    setToken(data.token);
    setUser(data.user);
    setEmailVerified(verified);
    // Broadcast to any tabs that opened before OTP was verified
    channelRef.current?.postMessage({
      type: 'TOKEN_RESPONSE',
      token: data.token,
      email_verified: verified ? '1' : '0',
    });
  };

  const register = async (formData) => {
    const res = await api().post('/auth/register', formData);
    sessionStorage.setItem('token', res.data.token);
    sessionStorage.setItem('email_verified', '0');
    setToken(res.data.token);
    setUser(res.data.user);
    setEmailVerified(false);
    return res.data;
  };

  const logout = async () => {
    // Broadcast logout to all tabs
    channelRef.current?.postMessage({ type: 'LOGOUT' });
    try {
      if (token) {
        await axios.post(`${API}/auth/logout`, {}, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
    } catch {
      // Ignore errors — clear local state regardless
    }
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('email_verified');
    setToken(null);
    setUser(null);
    setEmailVerified(false);
  };

  const updateUser = (userData) => {
    setUser(userData);
  };

  /** Re-fetch profile and return true if email is now verified */
  const refreshUser = async () => {
    try {
      const res = await api().get('/auth/profile');
      setUser(res.data.user);
      const verified = !!res.data.user.email_verified;
      setEmailVerified(verified);
      sessionStorage.setItem('email_verified', verified ? '1' : '0');
      return verified;
    } catch {
      return false;
    }
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, emailVerified, login, register, logout, completeOtpLogin, updateUser, refreshUser, api }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
